import type { AncillaryCabin } from '@voll-intelligence/domain';
import { useGenUiConfig } from '../../shared/ui/config.js';

// ⚠ Mirror contract: apps/integrations/sabre/src/seat_map/view.ts (producer)
// declares the same shape — and its `SeatMapResponse` envelope, whose
// `outcome: 'unavailable'` arrives here as `FlightSeatMapUnavailable`. Change
// them together. `FlightSeatMapQuery` mirrors its `SeatMapQuery`
// (src/sabre/seat_map_request.ts) the same way.

export interface FlightSeatMapColumn {
  letter: string; // 'A'
  aisleAfter: boolean; // render an aisle gap after this column
}

export interface FlightSeatMapSeat {
  column: string; // 'A'
  state: 'free' | 'paid' | 'extra' | 'occupied' | 'unavailable';
  price?: { amount: string; currency: string }; // decimal string, e.g. '45.00' BRL
  commercialName?: string; // ATPCO T191, e.g. 'COMFORT SEAT'
}

export interface FlightSeatMapRow {
  number: number;
  exit: boolean;
  overWing: boolean;
  seats: FlightSeatMapSeat[];
}

export interface FlightSeatMapView {
  aircraft?: string; // Equipment, e.g. '319'
  cabinClass?: string; // requested RBD, e.g. 'Y'
  columns: FlightSeatMapColumn[];
  rows: FlightSeatMapRow[];
  summary: {
    totalSeats: number;
    availableSeats: number; // free + paid + extra
    freeFromRow?: number; // first row containing a 'free' seat
    minPaid?: { amount: string; currency: string }; // cheapest positive price
  };
}

export interface FlightSeatMapQuery {
  carrier: string; // marketing carrier, 'LA'
  flightNumber: string; // '3340'
  departureDate: string; // 'YYYY-MM-DD' (airport-local calendar date)
  origin: string; // 'GRU'
  destination: string; // 'GIG'
  /** Cia OPERADORA quando a perna é codeshare (`leg.operatedBy` difere da
   *  marketing) — sem ela o Sabre recebe a cia marketing em <Operating> e nega
   *  o mapa deterministicamente (issue #2296). */
  operatingCarrier?: string; // 'AV'
  operatingFlightNumber?: string; // raro pré-booking; só faz sentido com a cia
  /** Cabine da tarifa da perna — sem ela o Sabre devolve o mapa da econômica
   *  mesmo numa tarifa executiva (issue #5949). */
  cabin?: AncillaryCabin;
}

/** O fornecedor não consegue produzir o mapa deste trecho, de forma
 *  determinística (issue #5248): a seção mostra o aviso definitivo de mapa não
 *  disponível antes da compra, SEM "tentar de novo". Espelho do
 *  `outcome: 'unavailable'` do `SeatMapResponse` da sabre-integration. */
export interface FlightSeatMapUnavailable {
  unavailable: 'supplier';
}

export interface FlightSeatMapPort {
  /** null = mapa indisponível para este voo (ausência REAL, a seção mostra o
   *  aviso definitivo). `FlightSeatMapUnavailable` = o fornecedor não produz o
   *  mapa (aviso definitivo, sem retry, #5248). Rejeição = falha recuperável
   *  (rede/transitória, #3099): a seção mostra "não foi possível carregar" +
   *  tentar de novo, até o limite de tentativas. */
  getSeatMap: (
    q: FlightSeatMapQuery,
  ) => Promise<FlightSeatMapView | FlightSeatMapUnavailable | null>;
}

export function isFlightSeatMapUnavailable(
  value: FlightSeatMapView | FlightSeatMapUnavailable | null,
): value is FlightSeatMapUnavailable {
  return value !== null && 'unavailable' in value;
}

export function useFlightSeatMapPort(): FlightSeatMapPort | null {
  return useGenUiConfig().ports?.flightSeatMap ?? null;
}
